import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calculator as CalcIcon,
  UploadCloud,
  FileText,
  Loader2,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { LANGUAGES, getLanguageName } from '../constants/languages';
import { DEFAULT_CHARS_PER_PAGE, formatMoney } from '../utils/rateCalc';
import RateSettingsSection from '../components/RateSettingsSection';
import InvoiceModal from '../components/InvoiceModal';
import './CalculatorPage.css';

const ACCEPT_TYPES =
  'image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
// One label per second-ish; loops so the last one stays visible even if the
// API is unusually slow. Deliberately chatty so the page feels alive.
const LOADING_MESSAGE_KEYS = [
  'loading.reading',
  'loading.counting',
  'loading.detecting',
  'loading.almost',
  'loading.finishing',
];
const LOADING_ROTATE_MS = 1400;

function formatNum(n) {
  return Math.round(n).toLocaleString();
}

function LanguageBadge({ code }) {
  const lang = LANGUAGES[code?.toLowerCase()];
  if (!lang) return <span>{code}</span>;
  return (
    <span className="calc-language-badge">
      <span className="calc-language-flag" aria-hidden="true">{lang.flag}</span>
      <span>{lang.name}</span>
    </span>
  );
}

// Uploader (drop-zone + click-to-pick). Fires `onFile` with a File object.
function Uploader({ onFile, disabled }) {
  const { t } = useTranslation('calculator');
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = () => inputRef.current?.click();
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div
      className={`calc-drop ${dragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={disabled ? undefined : pick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) pick(); }}
    >
      <UploadCloud size={48} className="calc-drop-icon" />
      <div className="calc-drop-title">{t('drop.title')}</div>
      <div className="calc-drop-hint">{t('drop.hint')}</div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function LoadingCard({ fileName }) {
  const { t } = useTranslation('calculator');
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setIdx((i) => Math.min(i + 1, LOADING_MESSAGE_KEYS.length - 1)),
      LOADING_ROTATE_MS
    );
    return () => clearInterval(id);
  }, []);
  return (
    <div className="calc-card calc-loading">
      <Loader2 size={40} className="calc-spinner" />
      <div className="calc-loading-msg">{t(LOADING_MESSAGE_KEYS[idx])}</div>
      <div className="calc-loading-file">
        <FileText size={14} />
        <span>{fileName}</span>
      </div>
      <div className="calc-loading-bar">
        <div className="calc-loading-bar-fill" />
      </div>
    </div>
  );
}

function countWords(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function ResultCard({ result, priceRows, hasAnyRates, rateSettings, fileName, onReset }) {
  const { t } = useTranslation('calculator');
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const pages =
    result.char_count / (rateSettings?.chars_per_page || DEFAULT_CHARS_PER_PAGE);
  const currency = rateSettings?.currency || '';
  const wordCount = countWords(result.extracted_text);
  const priceUnit = rateSettings?.unit === 'word' ? 'word' : 'page';
  return (
    <div className="calc-card calc-result">
      <div className="calc-metric calc-metric-primary">
        <div className="calc-metric-value">{formatNum(result.char_count)}</div>
        <div className="calc-metric-label">{t('result.characters')}</div>
      </div>

      <div className="calc-metric-row">
        <div className="calc-metric">
          <div className="calc-metric-value calc-metric-value-sm">
            {formatNum(wordCount)}
          </div>
          <div className="calc-metric-label">{t('result.words')}</div>
        </div>
        <div className="calc-metric">
          <div className="calc-metric-value calc-metric-value-sm">
            <LanguageBadge code={result.detected_language} />
          </div>
          <div className="calc-metric-label">{t('result.language')}</div>
        </div>
        <div className="calc-metric">
          <div className="calc-metric-value calc-metric-value-sm">
            {pages.toFixed(1)}
          </div>
          <div className="calc-metric-label">{t('result.pages')}</div>
        </div>
        <div className="calc-metric">
          <div className="calc-metric-value calc-metric-value-sm">
            {(result.latency_ms / 1000).toFixed(1)}s
          </div>
          <div className="calc-metric-label">{t('result.time')}</div>
        </div>
      </div>

      {result.truncated && (
        <div className="calc-truncation-warning">
          <AlertTriangle size={16} />
          {t('result.truncatedWarning', {
            total: result.total_pages,
            processed: result.page_count,
          })}
        </div>
      )}

      {priceRows.length > 0 ? (
        <div className="calc-price-list">
          <div className="calc-price-list-header">
            {t('result.priceByTarget')}
          </div>
          <table className="calc-price-table">
            <thead>
              <tr>
                <th>{t('result.target')}</th>
                <th className="calc-price-table-num">
                  {priceUnit === 'word' ? t('result.words') : t('result.pages')}
                </th>
                <th className="calc-price-table-num">
                  {priceUnit === 'word' ? t('result.rateShortWord') : t('result.rateShort')}
                </th>
                <th className="calc-price-table-num">{t('result.total')}</th>
              </tr>
            </thead>
            <tbody>
              {priceRows.map((row) => (
                <tr key={row.targetLang}>
                  <td>
                    <LanguageBadge code={row.targetLang} />
                  </td>
                  <td className="calc-price-table-num">
                    {priceUnit === 'word' ? formatNum(row.quantity) : row.quantity.toFixed(1)}
                  </td>
                  <td className="calc-price-table-num">
                    {formatMoney(row.rate)} {currency}
                  </td>
                  <td className="calc-price-table-num calc-price-table-total">
                    {formatMoney(row.price)} {currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="calc-price calc-price-empty">
          {hasAnyRates
            ? t('result.noRateForLanguage', {
                lang: getLanguageName(result.detected_language),
              })
            : t('result.setRateBelow')}
        </div>
      )}

      {priceRows.length > 0 && (
        <button
          type="button"
          className="btn btn-primary calc-invoice-btn"
          onClick={() => setInvoiceOpen(true)}
        >
          <FileText size={16} />
          {t('invoice.createCta')}
        </button>
      )}

      {result.extracted_text && (
        <details className="calc-extracted">
          <summary className="calc-extracted-summary">
            {t('result.showText')}
          </summary>
          <pre className="calc-extracted-text">{result.extracted_text}</pre>
        </details>
      )}

      <button type="button" className="btn btn-secondary calc-reset" onClick={onReset}>
        <RotateCcw size={16} />
        {t('result.uploadAnother')}
      </button>

      {invoiceOpen && (
        <InvoiceModal
          open={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          fileName={fileName}
          sourceLang={result.detected_language}
          priceRows={priceRows}
          currency={currency}
        />
      )}
    </div>
  );
}

function CalculatorPage() {
  const { t } = useTranslation('calculator');
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const rateSettings = user?.rate_settings || null;

  // Every rate the user has whose source_lang matches the detected doc
  // language. One row per target - because the price depends on target,
  // and the user might quote the same source across multiple target
  // languages (UA→EN, UA→PL, UA→DE …). Sorted by the order they appear
  // in the rate list so the user's Settings ordering wins.
  const priceRows = useMemo(() => {
    if (!result || !rateSettings || !rateSettings.rates?.length) return [];
    const src = (result.detected_language || '').toLowerCase();
    const unit = rateSettings.unit === 'word' ? 'word' : 'page';
    const perPage = rateSettings.chars_per_page || DEFAULT_CHARS_PER_PAGE;
    // Calculator always prices the source doc (single-doc quote), so unit just
    // switches the quantity between standard pages and words.
    const quantity =
      unit === 'word'
        ? countWords(result.extracted_text)
        : result.char_count / perPage;
    return rateSettings.rates
      .filter((r) => (r.source_lang || '').toLowerCase() === src)
      .map((r) => ({
        targetLang: r.target_lang,
        rate: r.price,
        unit,
        quantity,
        price: quantity * r.price,
      }));
  }, [result, rateSettings]);
  const hasAnyRates = !!(rateSettings && rateSettings.rates?.length);

  const onFile = async (f) => {
    setError(null);
    setResult(null);
    setFile(f);
    setLoading(true);
    try {
      const res = await api.analyzeForCalculator(f);
      setResult(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (detail && typeof detail === 'object' && detail.message) ||
        (typeof detail === 'string' ? detail : null) ||
        e?.message ||
        t('errors.generic');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="calc-page">
      <header className="calc-header">
        <div className="calc-header-icon">
          <CalcIcon size={28} />
        </div>
        <div>
          <h1 className="calc-title">
            {t('title')}
            <span className="page-free-badge">{t('common:badges.free')}</span>
          </h1>
          <p className="calc-subtitle">{t('subtitle')}</p>
        </div>
      </header>

      <div className="calc-body">
        {!loading && !result && !error && <Uploader onFile={onFile} />}
        {loading && <LoadingCard fileName={file?.name || ''} />}
        {error && (
          <div className="calc-card calc-error">
            <AlertTriangle size={24} />
            <div className="calc-error-title">{t('errors.title')}</div>
            <div className="calc-error-msg">{error}</div>
            <button type="button" className="btn btn-primary" onClick={reset}>
              {t('errors.tryAgain')}
            </button>
          </div>
        )}
        {result && !loading && (
          <ResultCard
            result={result}
            priceRows={priceRows}
            hasAnyRates={hasAnyRates}
            rateSettings={rateSettings}
            fileName={file?.name}
            onReset={reset}
          />
        )}
      </div>

      <div className="calc-rates">
        {/* showBasis={false}: the calculator only prices against the source
            volume (no target exists for a single-doc quote), so the basis
            selector would be confusing here. Kept in Settings. */}
        <RateSettingsSection showBasis={false} />
      </div>
    </div>
  );
}

export default CalculatorPage;
